if (!customElements.get('crimology-sticky-product')) {
  customElements.define('crimology-sticky-product', class extends HTMLElement {
    connectedCallback() {
      this.lifecycle?.abort();
      this.lifecycle = new AbortController();
      const options = { signal: this.lifecycle.signal };
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-sticky-add]')) this.addToCart();
      }, options);
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.measure());
        this.resizeObserver.observe(this);
      }
      if (window.Shopify?.designMode) {
        ['shopify:section:load', 'shopify:section:reorder'].forEach((name) => {
          document.addEventListener(name, () => this.bind(), options);
        });
        ['select', 'deselect'].forEach((action) => {
          document.addEventListener(`shopify:section:${action}`, (event) => {
            if (event.target !== this.closest('.shopify-section')) return;
            this.editorSelected = action === 'select';
            this.updateVisibility();
          }, options);
        });
        document.addEventListener('shopify:section:unload', (event) => {
          if (event.detail?.sectionId !== this.info?.dataset.section) return;
          this.releaseBindings();
          this.hidden = true;
        }, options);
      }
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.bind(), { ...options, once: true });
      } else this.bind();
    }

    disconnectedCallback() {
      this.lifecycle?.abort();
      this.resizeObserver?.disconnect();
      this.releaseBindings();
      document.documentElement.style.removeProperty('--crimology-sticky-height');
    }

    releaseBindings() {
      this.binding?.abort();
      this.intersectionObserver?.disconnect();
      this.stateObserver?.disconnect();
      this.unsubscribers?.forEach((unsubscribe) => unsubscribe());
      this.targets?.forEach(({ element, addedId, addedClass }) => {
        if (addedId && element.id === addedId) element.removeAttribute('id');
        if (addedClass) element.classList.remove('crimology-sticky-target');
      });
      this.targets = [];
      this.unsubscribers = [];
      this.pendingAdd = false;
    }

    bind() {
      if (!this.isConnected) return;
      this.releaseBindings();
      this.binding = new AbortController();
      const options = { signal: this.binding.signal };
      this.info = Array.from(document.querySelectorAll('product-info[id^="MainProduct-"]'))
        .find((element) => element.dataset.productId === this.dataset.productId);
      this.mainButton = this.info?.querySelector('.product-form__submit');
      this.addButton = this.querySelector('[data-sticky-add]');
      this.price = this.querySelector('[data-sticky-price]');
      this.status = this.querySelector('[data-sticky-status]');
      this.error = this.querySelector('[data-sticky-error]');
      if (!this.mainButton || !this.addButton) { this.hidden = true; return; }
      this.mainForm = this.mainButton.closest('product-form');
      this.priceRoot = this.info.querySelector('[id^="price-"]');
      const anchor = this.mainButton.closest('.product-form__buttons') || this.mainButton;
      this.resolveLinks();
      this.mainForm?.addEventListener('shopify:cart:error', (event) => {
        this.showError(event.error || event.detail?.error);
      }, options);
      this.mainForm?.addEventListener('submit', () => this.clearError(), { ...options, capture: true });
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        ['cartError', 'cartUpdate'].forEach((name) => {
          this.unsubscribers.push(subscribe(PUB_SUB_EVENTS[name], (event) => {
            if (!this.pendingAdd || event.source !== 'product-form' ||
              String(event.productVariantId) !== this.pendingVariantId) return;
            if (name === 'cartError') this.showError(event.errors || event.message);
            else { this.pendingAdd = false; this.clearError(); }
          }));
        });
      }
      this.stateObserver = new MutationObserver(() => this.sync());
      this.stateObserver.observe(this.mainButton, { attributes: true, childList: true, characterData: true, subtree: true });
      if (this.priceRoot) this.stateObserver.observe(this.priceRoot, { attributes: true, childList: true, characterData: true, subtree: true });
      this.sync();
      /* Desktop (lišta nahoře): objeví se až po odscrollování pod tlačítko Přidat do košíku; při scrollu nahoru se
         schová, aby nepřekrývala obsah, při scrollu dolů se vrátí. Pod 990 px (lišta dole) je vidět, kdykoli hlavní
         tlačítko není v záběru - tedy i hned po otevření detailu, kdy je tlačítko pod ohybem (zakladatel 22. 10. 2026);
         schová se jen s tlačítkem v záběru. */
      this.desktop = window.matchMedia('(min-width: 990px)');
      this.lastY = window.scrollY;
      this.scrollingUp = false;
      window.addEventListener('scroll', () => this.trackDirection(), { ...options, passive: true });
      this.past = this.outOfView(anchor.getBoundingClientRect());
      if ('IntersectionObserver' in window) {
        this.intersectionObserver = new IntersectionObserver(([entry]) => {
          this.past = this.outOfView(entry.boundingClientRect);
          this.updateVisibility();
        }, { threshold: 0 });
        this.intersectionObserver.observe(anchor);
        this.updateVisibility();
      } else this.hidden = true;
    }

    resolveLinks() {
      const selectors = { story: '.crimology-story', steps: '.crimology-steps', details: '.crimology-spis',
        contents: '.crimology-contents', compare: '.crimology-compare', faq: '.crimology-faq', bundles: '.crimology-bbbundle' };
      const main = this.info.closest('main') || document;
      this.querySelectorAll('[data-sticky-link]').forEach((link) => {
        const selector = selectors[link.dataset.target];
        const content = selector && main.querySelector(selector);
        const element = link.dataset.target === 'bundles' ? content : content?.closest('.shopify-section');
        link.hidden = !element;
        if (!element) return;
        const addedId = element.id ? null : `${this.id}-${link.dataset.target}`;
        const addedClass = !element.classList.contains('crimology-sticky-target');
        if (addedId) element.id = addedId;
        element.classList.add('crimology-sticky-target');
        this.targets.push({ element, addedId, addedClass });
        link.setAttribute('href', `#${element.id}`);
      });
    }

    sync() {
      const button = this.mainButton;
      if (!button?.isConnected) { this.hidden = true; return; }
      const id = button.form?.querySelector('[name="id"]')?.value;
      this.variantId = id;
      const busy = button.classList.contains('loading') || button.getAttribute('aria-busy') === 'true';
      const disabled = button.disabled || button.getAttribute('aria-disabled') === 'true';
      this.addButton.disabled = busy || disabled;
      this.addButton.setAttribute('aria-busy', String(busy));
      if (!busy) this.unavailable = disabled;
      if (this.status) {
        this.status.dataset.unavailable = String(!!this.unavailable);
        this.status.textContent = this.unavailable ? this.dataset.unavailableLabel : this.dataset.availableLabel;
      }
      const price = this.priceRoot?.querySelector('.price');
      const amount = price?.querySelector(price.classList.contains('price--on-sale') ?
        '.price__sale .price-item--sale' : '.price__regular span.price-item--regular');
      if (this.price && amount) {
        this.price.textContent = amount.textContent.trim();
        this.price.hidden = !!this.priceRoot.closest('.hidden, [hidden]');
      }
      const nativeError = this.mainForm?.querySelector('.product-form__error-message-wrapper:not([hidden]) .product-form__error-message');
      if (nativeError?.textContent.trim()) this.showError(nativeError.textContent.trim());
      else if (this.pendingAdd && this.wasBusy && !busy) this.showError();
      this.wasBusy = busy;
    }

    addToCart() {
      this.sync();
      if (!this.mainButton?.isConnected || this.addButton.disabled) return;
      this.clearError();
      this.pendingAdd = true;
      this.pendingVariantId = String(this.variantId);
      this.mainButton.click();
      this.sync();
    }

    clearError() {
      if (this.error) { this.error.textContent = ''; this.error.hidden = true; }
    }

    showError(message) {
      this.pendingAdd = false;
      if (this.error) {
        this.error.textContent = typeof message === 'string' && message.trim() ? message :
          window.cartStrings?.error || 'Přidání do košíku se nepodařilo. Zkuste to znovu.';
        this.error.hidden = false;
      } else this.mainForm?.scrollIntoView({ block: 'center' });
    }

    /* tlačítko mimo záběr: na desktopu jen nad horní hranou, pod 990 px i pod spodní hranou */
    outOfView(rect) {
      return rect.bottom <= 0 || (!this.desktop?.matches && rect.top >= window.innerHeight);
    }

    trackDirection() {
      const y = window.scrollY;
      const delta = y - this.lastY;
      if (Math.abs(delta) < 8) return;
      this.lastY = y;
      const up = delta < 0 && y > 0;
      if (up === this.scrollingUp) return;
      this.scrollingUp = up;
      this.updateVisibility();
    }

    updateVisibility() {
      const hideOnUp = !!this.desktop?.matches && this.scrollingUp;
      const show = this.mainButton?.isConnected && (this.editorSelected || (this.past && !hideOnUp));
      if (!show && this.contains(document.activeElement)) this.mainButton?.focus({ preventScroll: true });
      this.hidden = !show;
      this.measure();
    }

    measure() {
      if (this.isConnected && !this.hidden) document.documentElement.style.setProperty('--crimology-sticky-height', `${Math.ceil(this.getBoundingClientRect().height)}px`);
    }
  });
}
